import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Card, Button, Spinner, Alert, Badge } from 'react-bootstrap';
import ReactPlayer from 'react-player';  // 👈 IMPORT
import api from '../services/api';

const LessonView = () => {
    const { courseId, lessonId } = useParams();
    const navigate = useNavigate();
    const [lesson, setLesson] = useState(null);
    const [course, setCourse] = useState(null);
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [isCompleted, setIsCompleted] = useState(false);
    const [completing, setCompleting] = useState(false);

    useEffect(() => {
        fetchLesson();
        fetchCourse();
        fetchUser();
        checkProgress();
    }, [lessonId]);

    const fetchLesson = async () => {
        try {
            const response = await api.get(`/lessons/${lessonId}/`);
            setLesson(response.data);
        } catch (err) {
            setError('Nie udało się pobrać lekcji');
        }
    };

    const fetchCourse = async () => {
        try {
            const response = await api.get(`/courses/${courseId}/`);
            setCourse(response.data);
        } catch (err) {
            console.error('Błąd pobierania kursu:', err);
        }
    };

    const fetchUser = async () => {
        try {
            const response = await api.get('/users/me/');
            setUser(response.data);
        } catch (err) {
            console.error('Błąd pobierania użytkownika:', err);
        }
    };

    const checkProgress = async () => {
        try {
            const response = await api.get('/users/my_progress/');
            const progress = response.data.find(p => p.lesson === parseInt(lessonId));
            if (progress && progress.is_completed) {
                setIsCompleted(true);
            }
        } catch (err) {
            console.error('Błąd sprawdzania postępu:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleComplete = async () => {
        setCompleting(true);
        setError('');
        setSuccess('');

        try {
            await api.post('/progress/complete/', { lesson_id: parseInt(lessonId) });
            setIsCompleted(true);
            setSuccess('✅ Lekcja oznaczona jako ukończona!');
        } catch (err) {
            console.error('Błąd oznaczania lekcji:', err);
            setError('Nie udało się oznaczyć lekcji jako ukończonej');
        } finally {
            setCompleting(false);
        }
    };
    const getVideoEmbedUrl = (url) => {
        if (!url) return null;

        // YouTube
        if (url.includes('youtube.com/watch')) {
            const videoId = new URL(url).searchParams.get('v');
            return `https://www.youtube.com/embed/${videoId}`;
        }
        // YouTube Short
        if (url.includes('youtu.be')) {
            const videoId = url.split('/').pop();
            return `https://www.youtube.com/embed/${videoId}`;
        }
        // Vimeo
        if (url.includes('vimeo.com')) {
            const videoId = url.split('/').pop();
            return `https://player.vimeo.com/video/${videoId}`;
        }
        return null;
    };
    const getYouTubeId = (url) => {
        if (!url) return null;

        // https://www.youtube.com/watch?v=VIDEO_ID
        if (url.includes('youtube.com/watch')) {
            const params = new URLSearchParams(new URL(url).search);
            return params.get('v');
        }
        // https://youtu.be/VIDEO_ID
        if (url.includes('youtu.be')) {
            return url.split('/').pop().split('?')[0];
        }
        // https://www.youtube.com/embed/VIDEO_ID
        if (url.includes('youtube.com/embed')) {
            return url.split('/').pop().split('?')[0];
        }
        return null;
    };
    const isInstructor = user?.role === 'instructor';
    const isStudent = user?.role === 'student';

    if (loading) {
        return (
            <Container className="text-center mt-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3">Ładowanie lekcji...</p>
            </Container>
        );
    }

    if (error) {
        return (
            <Container className="mt-5">
                <Alert variant="danger">{error}</Alert>
                <Button
                    variant="outline-primary"
                    onClick={() => navigate(`/courses/${courseId}`)}
                    className="d-flex align-items-center gap-2">
                    ← Wróć do kursu
                </Button>
            </Container>
        );
    }

    if (!lesson) {
        return (
            <Container className="mt-5">
                <Alert variant="warning">Lekcja nie znaleziona</Alert>
                <Button
                    variant="outline-primary"
                    onClick={() => navigate(`/courses/${courseId}`)}
                    className="d-flex align-items-center gap-2">
                    ← Wróć do kursu
                </Button>
            </Container>
        );
    }

    return (
        <Container className="mt-4 mb-5">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <Button
                    variant="outline-primary"
                    onClick={() => navigate(`/courses/${courseId}`)}
                    className="d-flex align-items-center gap-2">
                    ← Wróć do kursu
                </Button>
                <Badge bg="secondary">
                    {course?.title || 'Kurs'}
                </Badge>
            </div>

            {error && <Alert variant="danger">{error}</Alert>}
            {success && <Alert variant="success">{success}</Alert>}

            <Card className="shadow-sm mb-4">
                <Card.Body>
                    <div className="d-flex justify-content-between align-items-start">
                        <div>
                            <h2>{lesson.title}</h2>
                            <div className="d-flex gap-3 mt-2">
                                <Badge bg="info">
                                    {lesson.content_type === 'video' ? '📹 Wideo' :
                                        lesson.content_type === 'text' ? '📝 Tekst' :
                                            lesson.content_type === 'quiz' ? '📊 Quiz' : 'Tekst'}
                                </Badge>
                                {lesson.duration_minutes && (
                                    <Badge bg="secondary">⏱️ {lesson.duration_minutes} min</Badge>
                                )}
                                {isCompleted && (
                                    <Badge bg="success">✅ Ukończone</Badge>
                                )}
                            </div>
                        </div>
                        {lesson.content_type === 'quiz' && (
                            <Button
                                variant="primary"
                                onClick={() => navigate(`/courses/${courseId}/lessons/${lessonId}/quiz`)}
                            >
                                📝 Rozpocznij quiz
                            </Button>
                        )}
                        {isInstructor && lesson.content_type === 'quiz' && (
                            <Button
                                variant="outline-primary"
                                onClick={() => navigate(`/courses/${courseId}/lessons/${lessonId}/quiz/edit`)}
                            >
                                📝 Edytuj quiz
                            </Button>
                        )}
                        {isStudent && (
                            <Button
                                variant={isCompleted ? "outline-warning" : "success"}
                                onClick={handleComplete}
                                disabled={completing}
                            >
                                {completing ? 'Zapisywanie...' :
                                    isCompleted ? '↩️ Cofnij' : '✅ Oznacz jako ukończone'}
                            </Button>
                        )}
                    </div>
                </Card.Body>
            </Card>

            <Card className="shadow-sm">
                <Card.Body>
                    {lesson.content_type === 'text' && lesson.content?.body && (
                        <div
                            className="lesson-content"
                            dangerouslySetInnerHTML={{ __html: lesson.content.body }}
                        />
                    )}
                    {lesson.content_type === 'video' && (
                        <div className="ratio ratio-16x9">
                            {lesson.video_file ? (
                                // Plik z serwera
                                <video
                                    controls
                                    src={lesson.video_file}
                                    style={{ width: '100%', height: '100%' }}
                                />
                            ) : lesson.content?.url && lesson.content.url.includes('youtube.com') ? (

                                <iframe
                                    src={`https://www.youtube.com/embed/${getYouTubeId(lesson.content.url)}?autoplay=0&modestbranding=1&rel=0`}
                                    title="YouTube video player"
                                    frameBorder="0"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                    allowFullScreen
                                    style={{ width: '100%', height: '100%' }}
                                />
                            ) : lesson.content?.url && lesson.content.url.includes('vimeo.com') ? (
                                // Vimeo
                                <iframe
                                    src={`https://player.vimeo.com/video/${lesson.content.url.split('/').pop()}`}
                                    title="Vimeo video player"
                                    frameBorder="0"
                                    allow="autoplay; fullscreen; picture-in-picture"
                                    allowFullScreen
                                    style={{ width: '100%', height: '100%' }}
                                />
                            ) : lesson.content?.url ? (
                                // Inny URL – próbuj jako MP4
                                <video
                                    controls
                                    src={lesson.content.url}
                                    style={{ width: '100%', height: '100%' }}
                                    onError={(e) => console.error('❌ Błąd video:', e)}
                                />
                            ) : (
                                <Alert variant="warning">Brak źródła wideo</Alert>
                            )}
                        </div>
                    )}

                </Card.Body>
            </Card>


        </Container>
    );
};

export default LessonView;